import { AudioCatalogue, type AudioFile } from './types';
import { getLogger } from '../../libs/logger';
import { type Logger } from 'loglevel';

type QueueItem = {
  key: AudioFile;
  url?: string;
  playing: boolean;
};

export class AudioNotificationPlayer {
  private static instance: AudioNotificationPlayer | null = null;

  private readonly audioElements: Map<AudioFile, HTMLAudioElement> = new Map();

  private readonly queue: QueueItem[] = [];

  private sinkId: string | null = null;

  private readonly logger: Logger;

  private delayBetweenNotifications = 1000; // Default delay of 1 second

  constructor() {
    this.logger = getLogger('AudioNotificationPlayer');
  }

  static getInstance(): AudioNotificationPlayer {
    if (AudioNotificationPlayer.instance === null) {
      AudioNotificationPlayer.instance = new AudioNotificationPlayer();
    }
    return AudioNotificationPlayer.instance;
  }

  private async loadAudio(audioFile: AudioFile): Promise<HTMLAudioElement> {
    const cached = this.audioElements.get(audioFile);
    if (cached) {
      return cached;
    }
    return new Promise((resolve, reject) => {
      const audioUrl = `/v2/audio/${AudioCatalogue[audioFile]}`;

      const audio = new Audio(audioUrl);
      audio.addEventListener(
        'canplaythrough',
        () => {
          this.audioElements.set(audioFile, audio);
          if (this.sinkId !== null) {
            try {
              void audio.setSinkId?.(this.sinkId);
            } catch {
              this.logger.warn(`Failed to set output device ${this.sinkId}`);
            }
          }
          resolve(audio);
        },
        { once: true },
      );
      audio.addEventListener(
        'error',
        (e) => {
          reject(e);
        },
        { once: true },
      );
      audio.load();
    });
  }

  /**
   * Set the preferred audio output device
   */
  async setAudioSink(sinkId: string): Promise<void> {
    this.sinkId = sinkId;
    try {
      const promises = [...this.audioElements.values()].map(async (audio) =>
        audio.setSinkId?.(sinkId),
      );
      await Promise.all(promises);
    } catch {
      this.logger.warn(`Failed to set output device ${this.sinkId}`);
    }
  }

  /**
   * Play an audio notification
   * @param key
   * @param url
   * @returns
   */
  async play(key: AudioFile): Promise<void> {
    if (this.queue.some((item) => item.key === key)) {
      return;
    }

    this.queue.push({ key, playing: false });
    await this.playNext();
  }

  /**
   * Play an audio preview immediately without queueing or delays
   * Useful for settings/preferences where users want instant feedback
   */
  async playPreview(key: AudioFile): Promise<void> {
    try {
      const audio = await this.loadAudio(key);
      audio.volume = 0.1;
      audio.currentTime = 0;
      await audio.play();
    } catch (error) {
      this.logger.warn(`Failed to play preview audio ${key}:`, error);
    }
  }

  private async playNext(): Promise<void> {
    if (this.queue.length === 0 || (this.queue[0]?.playing ?? false)) {
      return;
    }
    if (this.queue.length > 0) {
      const item = this.queue[0] as QueueItem;
      item.playing = true;
    }
    const { key } = this.queue[0] as QueueItem;

    let audio: HTMLAudioElement;

    try {
      audio = await this.loadAudio(key);
    } catch {
      this.queue.shift();
      await this.playNext();
      return;
    }
    audio.volume = 0.1;
    audio.currentTime = 0;

    try {
      const playEndedHandler = (): void => {
        setTimeout(() => {
          this.queue.shift();
          void this.playNext();
        }, this.delayBetweenNotifications);
      };

      audio.addEventListener('ended', playEndedHandler, { once: true });

      await audio.play();
    } catch (error) {
      this.logger.warn(`Failed to play audio ${key}:`, error);

      setTimeout(() => {
        this.queue.shift();
        void this.playNext();
      }, this.delayBetweenNotifications);
    }
  }

  setDelay(value: number): void {
    this.delayBetweenNotifications = value;
  }
}

export default AudioNotificationPlayer.getInstance();
